import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Target, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import { KOLProfile } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { fromSlug } from '../../lib/slug';
import { formatPercentOneDecimal } from '../../lib/utils';
import { kolPlatformFollowers, kolTotalFollowers } from '../../lib/kolFollowers';
import {
  SocialHandleLink,
  instagramProfileUrl,
  tiktokProfileUrl,
  youtubeProfileUrl,
  facebookProfileUrl,
} from '../../lib/socialProfileLinks';

export default function CorporateKOLDetailPage() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [kol, setKol] = useState<KOLProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKOL();
  }, [id]);

  const loadKOL = async () => {
    try {
      const response = await api.get(`/kols/${id}`);
      setKol(response.data);
    } catch (error) {
      console.error('Failed to load KOL:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!kol) {
    return <div className="text-center py-12 text-gray-500">KOL not found</div>;
  }

  const platforms = kol.platforms as any;
  const audience = kol.audience as any;

  const getEngagementRate = (): number => {
    const rates = [];
    if (platforms?.instagram?.engagement ?? platforms?.instagram?.engagementRate)
      rates.push(platforms.instagram.engagement ?? platforms.instagram.engagementRate);
    if (platforms?.tiktok?.engagement ?? platforms?.tiktok?.engagementRate)
      rates.push(platforms.tiktok.engagement ?? platforms.tiktok.engagementRate);
    if (platforms?.youtube?.engagement ?? platforms?.youtube?.engagementRate)
      rates.push(platforms.youtube.engagement ?? platforms.youtube.engagementRate);
    if (platforms?.facebook?.engagement ?? platforms?.facebook?.engagementRate)
      rates.push(platforms.facebook.engagement ?? platforms.facebook.engagementRate);

    if (rates.length === 0) return 0;
    return rates.reduce((a, b) => a + b, 0) / rates.length;
  };

  const linkCls =
    'font-medium text-gray-900 hover:text-purple-600 underline-offset-2 hover:underline';
  const igHandleRaw = kol.instagramHandle || platforms?.instagram?.handle;
  const igHref =
    kol.instagramProfileUrl ||
    (platforms?.instagram as { url?: string } | undefined)?.url ||
    instagramProfileUrl(igHandleRaw);
  const igDisplay = (() => {
    if (igHandleRaw == null || String(igHandleRaw).trim() === '—') return '—';
    return `@${String(igHandleRaw).replace(/^@+/, '')}`;
  })();
  const ttHandleRaw = kol.tiktokHandle || platforms?.tiktok?.handle;
  const ttHref =
    kol.tiktokProfileUrl ||
    (platforms?.tiktok as { url?: string } | undefined)?.url ||
    tiktokProfileUrl(ttHandleRaw);
  const ttDisplay = (() => {
    if (ttHandleRaw == null || String(ttHandleRaw).trim() === '—') return '—';
    return `@${String(ttHandleRaw).replace(/^@+/, '')}`;
  })();
  const ytChannelRaw = kol.youtubeChannel || platforms?.youtube?.handle;
  const ytHref =
    kol.youtubeChannelUrl ||
    (platforms?.youtube as { url?: string } | undefined)?.url ||
    youtubeProfileUrl(ytChannelRaw);
  const fbHandleRaw = kol.facebookHandle || platforms?.facebook?.handle;
  const fbHref =
    kol.facebookProfileUrl ||
    (platforms?.facebook as { url?: string } | undefined)?.url ||
    facebookProfileUrl(fbHandleRaw);
  const fbDisplay = (() => {
    if (fbHandleRaw == null || String(fbHandleRaw).trim() === '—') return '—';
    return `@${String(fbHandleRaw).replace(/^@+/, '')}`;
  })();

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-1 text-sm text-gray-500">
        <button onClick={() => navigate('/brand/kols')} className="hover:text-purple-600 transition-colors">KOLs</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium truncate max-w-md">{kol.name}</span>
      </nav>

      <div className="flex items-center justify-between">
        <Button onClick={() => navigate('/brand/kols')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to KOLs
        </Button>
      </div>

      {/* Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-4xl font-bold">
              {kol.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="page-title">{kol.name}</h1>
              {kol.gender && (
                <p className="text-sm text-gray-500 mt-1 capitalize">{kol.gender.replace(/_/g, ' ').toLowerCase()}</p>
              )}

              {/* Platform Icons */}
              <div className="flex gap-3 mt-3">
                {(platforms?.instagram || kol.instagramHandle || kol.instagramProfileUrl || kol.instagramFollowers != null) && (
                  <div className="flex items-center gap-2 text-pink-600">
                    <span className="text-2xl">📷</span>
                    <span className="text-sm font-medium">Instagram</span>
                  </div>
                )}
                {(platforms?.tiktok || kol.tiktokHandle || kol.tiktokProfileUrl || kol.tiktokFollowers != null) && (
                  <div className="flex items-center gap-2 text-black">
                    <span className="text-2xl">🎵</span>
                    <span className="text-sm font-medium">TikTok</span>
                  </div>
                )}
                {(platforms?.youtube || kol.youtubeChannel || kol.youtubeChannelUrl || kol.youtubeFollowers != null) && (
                  <div className="flex items-center gap-2 text-red-600">
                    <span className="text-2xl">▶️</span>
                    <span className="text-sm font-medium">YouTube</span>
                  </div>
                )}
                {(platforms?.facebook || kol.facebookHandle || kol.facebookProfileUrl || kol.facebookFollowers != null) && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <span className="text-2xl">👥</span>
                    <span className="text-sm font-medium">Facebook</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {kol.tags?.map((tagRel: any) => (
                  <Badge key={tagRel.tag.id} variant="secondary">
                    {tagRel.tag.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">
                  {kolTotalFollowers(kol).toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Total Reach</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{formatPercentOneDecimal(getEngagementRate())}</div>
                <div className="text-xs text-gray-600">Avg Engagement</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <Target className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">
                  {Object.keys(platforms || {}).length}
                </div>
                <div className="text-xs text-gray-600">Platforms</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(platforms?.instagram || kol.instagramHandle || kol.instagramProfileUrl || kol.instagramFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">📷</span>
                Instagram
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Handle</span>
                  <SocialHandleLink href={igHref} className={linkCls}>
                    {igDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Followers</span>
                  <span className="font-medium">{kolPlatformFollowers(kol, 'instagram').toLocaleString()}</span>
                </div>
                {(platforms?.instagram?.engagement ?? platforms?.instagram?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagement</span>
                    <span className="font-medium">
                      {formatPercentOneDecimal(
                        platforms.instagram.engagement ?? platforms.instagram.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.tiktok || kol.tiktokHandle || kol.tiktokProfileUrl || kol.tiktokFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🎵</span>
                TikTok
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Handle</span>
                  <SocialHandleLink href={ttHref} className={linkCls}>
                    {ttDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Followers</span>
                  <span className="font-medium">{kolPlatformFollowers(kol, 'tiktok').toLocaleString()}</span>
                </div>
                {(platforms?.tiktok?.engagement ?? platforms?.tiktok?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagement</span>
                    <span className="font-medium">
                      {formatPercentOneDecimal(
                        platforms.tiktok.engagement ?? platforms.tiktok.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.youtube || kol.youtubeChannel || kol.youtubeChannelUrl || kol.youtubeFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">▶️</span>
                YouTube
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Channel</span>
                  <SocialHandleLink
                    href={ytHref}
                    className={`${linkCls} break-all text-right max-w-[min(100%,12rem)]`}
                  >
                    {ytChannelRaw && String(ytChannelRaw).trim() !== '—' ? String(ytChannelRaw).trim() : '—'}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subscribers</span>
                  <span className="font-medium">{kolPlatformFollowers(kol, 'youtube').toLocaleString()}</span>
                </div>
                {(platforms?.youtube?.engagement ?? platforms?.youtube?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagement</span>
                    <span className="font-medium">
                      {formatPercentOneDecimal(
                        platforms.youtube.engagement ?? platforms.youtube.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {(platforms?.facebook || kol.facebookHandle || kol.facebookProfileUrl || kol.facebookFollowers != null) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">👥</span>
                Facebook
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Handle</span>
                  <SocialHandleLink href={fbHref} className={linkCls}>
                    {fbDisplay}
                  </SocialHandleLink>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Followers</span>
                  <span className="font-medium">{kolPlatformFollowers(kol, 'facebook').toLocaleString()}</span>
                </div>
                {(platforms?.facebook?.engagement ?? platforms?.facebook?.engagementRate) != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Engagement</span>
                    <span className="font-medium">
                      {formatPercentOneDecimal(
                        platforms.facebook.engagement ?? platforms.facebook.engagementRate
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Audience Demographics */}
      <Card>
        <CardHeader>
          <CardTitle>Audience Demographics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Gender */}
            {audience?.demographics && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Gender Distribution</h3>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Female</span>
                      <span className="font-medium">{audience.demographics.female || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-pink-500 h-2 rounded-full"
                        style={{ width: `${audience.demographics.female || 0}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Male</span>
                      <span className="font-medium">{audience.demographics.male || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${audience.demographics.male || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Age Groups */}
            {audience?.ageGroups && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Age Groups</h3>
                <div className="space-y-2">
                  {Object.entries(audience.ageGroups)
                    .sort((a: any, b: any) => b[1] - a[1])
                    .map(([group, percentage]: any) => (
                      <div key={group}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{group}</span>
                          <span className="font-medium">{percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Ethnic Mix */}
          {audience?.ethnicMix && (
            <div className="mt-8">
              <h3 className="font-semibold text-gray-900 mb-3">Ethnic Mix</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(audience.ethnicMix)
                  .filter(([_, percentage]: any) => percentage > 0)
                  .map(([ethnicity, percentage]: any) => (
                    <div key={ethnicity} className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">{percentage}%</div>
                      <div className="text-sm text-gray-600 capitalize">{ethnicity}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes (if any) */}
      {kol.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{kol.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Privacy Notice */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-900">
            <strong>Privacy Notice:</strong> For privacy reasons, KOL contact information (email and phone) is not displayed.
            All communications with KOLs are managed through your agency partner.
          </div>
        </div>
      </div>
    </div>
  );
}

